export type StockPickHoldingType = 'SHORT_TERM_3M' | 'SHORT_TERM_6M' | 'LONG_TERM_DIVIDEND';

export type StockPlanStatus =
  | 'CREATED'
  | 'ANALYSIS'
  | 'WAITING_FOR_EXECUTION'
  | 'EXECUTED'
  | 'DEPLOYED'
  | 'HARVESTED';

export type StockPickCapClass = 'LARGE' | 'MID' | 'SMALL' | 'MICRO';

export interface StockPlanExternalLink {
  id?: string;
  stockPlanId?: string;
  url: string;
  label?: string;
  sortOrder?: number;
}

export interface StockPlanComment {
  id: string;
  stockPlanId: string;
  body: string;
  createdBy?: string;
  createdDate?: string;
  updatedDate?: string;
}

export interface StockPlanSummary {
  id: string;
  planCode: string;
  stockProfileId: string;
  planLabel?: string;
  holdingType: StockPickHoldingType;
  plannedCapital?: number;
  planStatus?: StockPlanStatus;
  createdDate?: string;
  updatedDate?: string;
}

export interface StockProfile {
  id?: string;
  stockName?: string;
  nseCode?: string;
  bseCode?: string;
  domain?: string;
  cap?: StockPickCapClass;
  createdBy?: string;
  createdDate?: string;
  updatedDate?: string;
  planCount?: number;
  plans?: StockPlanSummary[];
}

export interface StockPlan {
  id?: string;
  planCode?: string;
  stockProfileId?: string;
  planLabel?: string;
  planAndReasons: string;
  holdingType: StockPickHoldingType;
  plannedCapital?: number;
  exitStrategy?: string;
  riskMitigation?: string;
  planStatus?: StockPlanStatus;
  createdBy?: string;
  createdDate?: string;
  updatedDate?: string;
  profile?: StockProfile;
  comments?: StockPlanComment[];
  externalLinks?: StockPlanExternalLink[];
  capitalAllocations?: PlanCapitalAllocation[];
  totalAllocatedCapital?: number;
  result?: StockPlanResult;
}

export interface StockPlanResult {
  id?: string;
  stockPlanId?: string;
  investedAmount?: number;
  quantity?: number;
  taxAmount?: number;
  /** From sells only (auto). */
  calculatedProfitAmount?: number;
  /** Admin override; null = use calculated. */
  profitOverride?: number | null;
  /** Effective profit (override or calculated). */
  profitAmount?: number;
  learning?: string;
  resultDate?: string;
  createdDate?: string;
  updatedDate?: string;
}

export interface PlanCapitalAllocation {
  id?: string;
  stockPlanId?: string;
  capitalPartnerId: string;
  partnerName?: string;
  planCode?: string;
  allocatedAmount: number;
  note?: string;
  createdDate?: string;
}

export interface CapitalPartner {
  id?: string;
  name: string;
  email?: string;
  phone?: string;
  notes?: string;
  status?: 'ACTIVE' | 'INACTIVE';
  portalAccessEnabled?: boolean;
  hasPortalPassword?: boolean;
  totalContributed?: number;
  totalAllocated?: number;
  availableBalance?: number;
  createdDate?: string;
  contributions?: CapitalContribution[];
  planAllocations?: PlanCapitalAllocation[];
}

export interface CapitalContribution {
  id?: string;
  capitalPartnerId?: string;
  amount: number;
  contributionDate: string;
  referenceNote?: string;
  createdDate?: string;
}

export interface CapitalManagementSummary {
  totalContributed: number;
  totalAllocatedOnPlans: number;
  totalAvailable: number;
  partners: CapitalPartner[];
}

export interface CapitalsDashboardTopPartner {
  id: string;
  name: string;
  totalContributed: number;
  totalAllocated: number;
  availableBalance: number;
}

export interface CapitalsDashboardRecentContribution {
  contributionId: string;
  partnerId: string;
  partnerName?: string;
  amount: number;
  contributionDate: string;
  referenceNote?: string;
}

/**
 * Read model for the admin Capital Partners dashboard tile. Returned from
 * `GET /api/v1/capitals/dashboard`.
 */
export interface CapitalsDashboard {
  totalPartners: number;
  activePartners: number;
  inactivePartners: number;
  partnersWithPortalAccess: number;
  partnersWithContributions: number;
  totalCapitalContributed: number;
  totalAllocatedOnPlans: number;
  totalAvailable: number;
  totalContributionCount: number;
  latestContributionDate?: string;
  averageContributionAmount: number;
  deployedRatioPct: number;
  topPartners: CapitalsDashboardTopPartner[];
  recentContributions: CapitalsDashboardRecentContribution[];
}

export function emptyPlanResult(): StockPlanResult {
  return { resultDate: new Date().toISOString().slice(0, 10) };
}

export function emptyAllocation(): PlanCapitalAllocation {
  return { capitalPartnerId: '', allocatedAmount: 0, note: '' };
}

export const HOLDING_TYPE_OPTIONS: { value: StockPickHoldingType; label: string }[] = [
  { value: 'SHORT_TERM_3M', label: 'Short term (3 months)' },
  { value: 'SHORT_TERM_6M', label: 'Short term (6 months)' },
  { value: 'LONG_TERM_DIVIDEND', label: 'Long term — dividend focused' },
];

export const PLAN_STATUS_OPTIONS: { value: StockPlanStatus; label: string }[] = [
  { value: 'CREATED', label: 'Created' },
  { value: 'ANALYSIS', label: 'Analysis' },
  { value: 'WAITING_FOR_EXECUTION', label: 'Waiting for execution' },
  { value: 'EXECUTED', label: 'Executed' },
  { value: 'DEPLOYED', label: 'Deployed' },
  { value: 'HARVESTED', label: 'Harvested' },
];

export const CAP_CLASS_OPTIONS: { value: StockPickCapClass; label: string }[] = [
  { value: 'LARGE', label: 'Large cap' },
  { value: 'MID', label: 'Mid cap' },
  { value: 'SMALL', label: 'Small cap' },
  { value: 'MICRO', label: 'Micro cap' },
];

export const STOCK_DOMAIN_OPTIONS: string[] = [
  'Banking & Finance',
  'IT & Software',
  'Pharma & Healthcare',
  'FMCG',
  'Energy & Oil',
  'Metals & Mining',
  'Automobile',
  'Infrastructure & Construction',
  'Telecom',
  'Consumer Durables',
  'Chemicals',
  'Real Estate',
  'Media & Entertainment',
  'Agriculture & Agri-chem',
  'Defence & Aerospace',
  'Capital Goods',
  'Textiles',
  'Logistics & Transport',
  'Insurance',
  'Others',
];

export function capClassLabel(value?: string): string {
  return CAP_CLASS_OPTIONS.find((o) => o.value === value)?.label ?? value ?? '-';
}

export function holdingTypeLabel(value?: string): string {
  return HOLDING_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value ?? '-';
}

export function planStatusLabel(value?: string): string {
  return PLAN_STATUS_OPTIONS.find((o) => o.value === value)?.label ?? value ?? 'Created';
}

export function planStatusIndex(value?: string): number {
  const idx = PLAN_STATUS_OPTIONS.findIndex((o) => o.value === value);
  return idx >= 0 ? idx : 0;
}

export function planStatusChipColor(
  value?: string
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
  switch (value) {
    case 'ANALYSIS':
      return 'info';
    case 'WAITING_FOR_EXECUTION':
      return 'warning';
    case 'EXECUTED':
      return 'secondary';
    case 'DEPLOYED':
      return 'primary';
    case 'HARVESTED':
      return 'success';
    default:
      return 'default';
  }
}

export function stockProfileDisplayName(profile: Pick<StockProfile, 'stockName' | 'nseCode' | 'bseCode'>): string {
  if (profile.stockName?.trim()) {
    return profile.stockName.trim();
  }
  const codes = [profile.nseCode, profile.bseCode].filter(Boolean).join(' / ');
  return codes || 'Untitled stock';
}

export function planDisplayTitle(plan: Pick<StockPlan, 'planCode' | 'planLabel'>): string {
  if (plan.planLabel?.trim()) {
    return `${plan.planCode} — ${plan.planLabel.trim()}`;
  }
  return plan.planCode ?? 'Plan';
}

export function newExternalLink(): StockPlanExternalLink {
  return { url: '', label: '' };
}

export function emptyProfile(): StockProfile {
  return { nseCode: '', bseCode: '', domain: '', cap: undefined };
}

export function emptyPlan(): StockPlan {
  return {
    planAndReasons: '',
    holdingType: 'SHORT_TERM_3M',
    planStatus: 'CREATED',
    externalLinks: [newExternalLink()],
  };
}
